const COMPOUND_SURNAMES = [
  ["AU", "YEUNG"],
  ["SZE", "TO"],
  ["CHU", "KOT"],
  ["SHEUNG", "KWAN"],
  ["WONG", "PO"],
  ["SZETO"],
  ["CHUKO"],
];

const CHINESE_COMPOUND_SURNAMES = [
  "歐陽",
  "司馬",
  "上官",
  "夏侯",
  "諸葛",
  "東方",
  "皇甫",
  "尉遲",
  "公孫",
  "慕容",
  "軒轅",
  "令狐",
  "宇文",
  "長孫",
  "司空",
  "司徒",
  "南宮",
  "東門",
  "西門",
  "公羊",
  "百里",
  "東郭",
  "南門",
  "微生",
  "公冶",
  "宗政",
  "濮陽",
  "淳于",
  "仲孫",
  "鍾離",
  "公孫",
  "申屠",
  "太叔",
  "獨孤",
  "聞人",
  "赫連",
  "澹台",
  "萬俟",
  "第五",
];

const CJK_REGEX = /[\u4e00-\u9fff\u3400-\u4dbf]/;

const normalizeWhitespace = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");

const isCjkCharacter = (char) => CJK_REGEX.test(char);

const extractCjkCharacters = (name) =>
  [...normalizeWhitespace(name)].filter(isCjkCharacter);

export const isPrimarilyCjkName = (name) => {
  const normalized = normalizeWhitespace(name);
  if (!normalized) return false;

  const compact = normalized.replace(/\s+/g, "");
  if (!compact) return false;

  const cjkCount = [...compact].filter(isCjkCharacter).length;
  return cjkCount === compact.length && cjkCount >= 2;
};

const normalizeNameForComparison = (name) => {
  const normalized = normalizeWhitespace(name);
  if (!normalized) return "";
  if (isPrimarilyCjkName(normalized)) {
    return extractCjkCharacters(normalized).join("");
  }
  return normalized.toUpperCase();
};

const tokenizeName = (name) =>
  normalizeWhitespace(name)
    .toUpperCase()
    .replace(/-/g, " ")
    .split(" ")
    .filter(Boolean);

export const composeClientName = (surname, givenName) => {
  const s = normalizeWhitespace(surname);
  const g = normalizeWhitespace(givenName);
  if (!s && !g) return "";

  if (isPrimarilyCjkName(`${s}${g}`)) {
    return `${s}${g}`;
  }

  return [s, g]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
};

const SALUTATION_TOKENS = new Set([
  "MR",
  "MRS",
  "MS",
  "MISS",
  "DR",
  "CORPORATE",
  "OTHER",
]);

const isSalutationToken = (value) => {
  const token = normalizeWhitespace(value)
    .toUpperCase()
    .replace(/\./g, "");
  return SALUTATION_TOKENS.has(token);
};

export const stripSalutationPrefix = (name) => {
  const tokens = tokenizeName(name);
  while (tokens.length > 1 && isSalutationToken(tokens[0])) {
    tokens.shift();
  }
  return tokens.join(" ");
};

export const splitChineseClientName = (name) => {
  const chars = extractCjkCharacters(name);
  if (!chars.length) {
    return { surname: "", givenName: "" };
  }
  if (chars.length === 1) {
    return { surname: chars[0], givenName: "" };
  }

  const full = chars.join("");
  const sortedCompounds = [...CHINESE_COMPOUND_SURNAMES].sort(
    (a, b) => b.length - a.length
  );

  for (const compound of sortedCompounds) {
    if (full.startsWith(compound) && full.length > compound.length) {
      return {
        surname: compound,
        givenName: full.slice(compound.length),
      };
    }
  }

  return {
    surname: chars[0],
    givenName: chars.slice(1).join(""),
  };
};

const namePartsMatchComposedName = (surname, givenName, name) => {
  const composed = composeClientName(surname, givenName);
  if (!composed) return false;
  return (
    normalizeNameForComparison(composed) === normalizeNameForComparison(name)
  );
};

export const needsClientNameHydration = (client) => {
  if (!isNonCorporateClientType(client?.type)) return false;
  if (!normalizeWhitespace(client?.name)) return false;

  const surname = normalizeWhitespace(client?.surname);
  const givenName = normalizeWhitespace(client?.givenName);

  if (!surname && !givenName) return true;
  if (surname && isSalutationToken(surname) && !givenName) return true;

  const expected = splitClientName(client.name);
  if (expected.givenName && !givenName) return true;
  if (
    expected.surname &&
    surname === expected.surname &&
    expected.givenName &&
    givenName !== expected.givenName
  ) {
    return true;
  }
  if (
    normalizeNameForComparison(surname) === normalizeNameForComparison(client.name) &&
    expected.givenName
  ) {
    return true;
  }

  if (!namePartsMatchComposedName(surname, givenName, client.name)) return true;
  return false;
};

export const splitClientName = (name) => {
  const stripped = stripSalutationPrefix(name);
  const normalized = normalizeWhitespace(stripped);

  if (isPrimarilyCjkName(normalized)) {
    return splitChineseClientName(normalized);
  }

  const tokens = tokenizeName(stripped);
  if (!tokens.length) {
    return { surname: "", givenName: "" };
  }

  const sortedCompounds = [...COMPOUND_SURNAMES].sort(
    (a, b) => b.length - a.length
  );

  for (const compound of sortedCompounds) {
    if (tokens.length < compound.length) continue;
    const matches = compound.every((token, index) => tokens[index] === token);
    if (!matches) continue;

    return {
      surname: compound.join(" "),
      givenName: tokens.slice(compound.length).join(" "),
    };
  }

  if (tokens.length === 1) {
    return { surname: tokens[0], givenName: "" };
  }

  return {
    surname: tokens[0],
    givenName: tokens.slice(1).join(" "),
  };
};

export const isNonCorporateClientType = (type) => type !== "CORPORATE";

export const hydrateClientNameFields = (client = {}) => {
  if (!needsClientNameHydration(client)) return client;

  const { surname, givenName } = splitClientName(client.name);
  return { ...client, surname, givenName };
};

export const prepareClientNameForSave = (values = {}) => {
  if (!isNonCorporateClientType(values.type)) return values;
  if (values.surname === undefined && values.givenName === undefined) {
    return values;
  }
  return {
    ...values,
    name: composeClientName(values.surname, values.givenName),
  };
};
